/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  FormFlowState,
  OfficeAccessService,
} from '@gh/builder-shared';
import { createContextRoute } from '@gh/common-internal-shared';
import {
  ActionFlow,
  getRouteInitData,
  getRouteParentTitle,
  queryFormMode,
  queryFormModeCreate,
  State,
} from '@gh/common-shared';
import { TranslationService, TranslationTupleOrId } from '@gh/core-mls';
import { FormMode } from '@gh/core-ui';
import { asString } from '@gh/core-util';
import { of$ } from '@gh/rx';
import { map$ } from '@gh/rx/operators';

import { Observable } from 'rxjs';

import { LeadDetailsBusinessService } from '../../resources/lead-details-business.service';
import { LeadDetailsCommandService } from './lead-details-command.service';
import { LeadDetailsDcTabComponent } from './lead-details-dc-tab/lead-details-dc-tab.component';
import { LeadDetailsFormState } from './lead-details-form.service';
import { LeadDetailsSfTabComponent } from './lead-details-sf-tab/lead-details-sf-tab.component';
import { leadDetailsReducer } from './lead-details.reducers';
import {
  LEAD_DETAILS_INIT_STATE,
  LeadDetailsState,
  LeadDetailsTabName,
  queryLeadContactName,
  queryLeadId,
  queryLeadSelectedTab,
} from './lead-details.state';

export function leadDetailsStateFactory(): State<LeadDetailsState> {
  return new State(LEAD_DETAILS_INIT_STATE, leadDetailsReducer);
}

@Component({
  standalone: false,
  selector: 'gh-lead-details-tab-container',
  templateUrl: 'lead-details-tab-container.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: State, useFactory: leadDetailsStateFactory },
    LeadDetailsCommandService,
    LeadDetailsFormState,
  ],
})
export class LeadDetailsTabContainerComponent extends AbstractFormComponent<LeadDetailsState> implements OnInit {
  selectedTab$: Observable<LeadDetailsTabName>;

  name$: Observable<string>;

  /** Multi-office: Reassign is hidden for a Salesperson in a membership office. */
  reassignRestricted$: Observable<boolean>;

  routes = [
    createContextRoute({
      component: LeadDetailsDcTabComponent,
      path: LeadDetailsTabName.DetailsAndCommunications,
      init: {
        service: LeadDetailsBusinessService,
        selector: 'initDcTab',
      },
    }),
    createContextRoute({
      component: LeadDetailsSfTabComponent,
      path: LeadDetailsTabName.SalesFolders,
      init: {
        service: LeadDetailsBusinessService,
        selector: 'initSalesFolderTab',
      },
    }),
  ];

  constructor(private route: ActivatedRoute,
              private translations: TranslationService,
              public form: LeadDetailsFormState,
              private state: State<LeadDetailsState>,
              private angRouter: Router,
              private commands: LeadDetailsCommandService,
              private navigationService: BuilderNavigationService,
              public business: LeadDetailsBusinessService,
              private builderContext: BuilderUserContext,
              private officeAccessService: OfficeAccessService) {
    super();
  }

  get formModeTitle(): string {
    return {
      [FormMode.CreateMode]: 'lbl_lead_create',
      [FormMode.EditMode]: 'lbl_lead_edit',
      [FormMode.ViewMode]: 'lbl_lead_view',
    }[this.state.project(queryFormMode)];
  }

  get completeFlow(): ActionFlow<FormFlowState<LeadDetailsState>> {
    return this.commands.completeFlow();
  }

  get parentTitle(): TranslationTupleOrId {
    return getRouteParentTitle(this.route) || 'lbl_lead_list';
  }

  ngOnInit(): void {
    const { details, tab, salesFolderId } = getRouteInitData(this.route);
    this.form.build(details);

    if (tab === LeadDetailsTabName.SalesFolders) {
      this.commands.selectTab(tab, salesFolderId);
    }

    this.name$ = this.state.project(queryFormModeCreate) ?
      of$(this.translations.get('lbl_lead_create'))
      : this.state.select(queryLeadContactName).pipe(map$(asString));

    this.selectedTab$ = this.state.select(queryLeadSelectedTab);
    this.reassignRestricted$ = this.officeAccessService.isReassignRestricted(this.builderContext);
  }

  onSelectedTabChanged(tab: LeadDetailsTabName): void {
    const queryParams: Params = { tab };

    this.angRouter.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
      },
    );
    this.commands.selectTab(tab);
  }

  reassign(): void {
    this.commands.reassign();
  }

  goToAel(): void {
    this.navigationService.goToAel({
      label: 'lbl_lead_details',
      entityType: 'LEAD',
      entityId: this.state.project(queryLeadId),
    });
  }
}
